import React, { useCallback, useMemo, useState } from 'react';
import { Button, Text, Flex, Box, Stack, Group, Tooltip } from '@mantine/core';
import {
    IconRefresh,
    IconCirclePlus,
    IconEye,
    IconEyeOff,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import {
    getSurveyItemsColumns,
    CompetitorDrawerData,
} from '../../pages/price-surveys/columns';
import {
    getErrorMessage,
    getSurveyAdvertisingZoneId,
    isSurveyInDivisionalManagerReview,
    isSurveyStoreManagerEditingLocked,
} from '../../pages/price-surveys/helpers';
import { SurveyItemRow } from '../../pages/price-surveys/types';
import { readSurveyItemLivePricing } from '../../helpers/surveyItemLivePricing';
import {
    User,
    PriceSurvey,
    PriceSurveyCompetitor,
} from '../../codegen/graphql';
import { checkUserRole, isUserAdmin } from '../../utility/userRoles';
import { UserRole } from '../../types/users';
import type { SurveyPriceCell } from '../../hooks/useSurveyPrices';
import { useProductSalesForSurveyItems } from '../../hooks/useProductSalesForSurveyItems';
import { useSalesDataMaintenance } from '../../hooks/useSalesDataMaintenance';
import type { ProductSalesScopeInput } from '../../hooks/useProductSalesScope';
import { PageZoomHost } from '../../context/PageZoomContext';
import { DivisionalManagerSurveyProgressProvider } from '../../context/DivisionalManagerSurveyProgressContext';
import { useRequestDrawer } from '../../context/RequestDrawerContext';
import { useSurveyPricesContext } from '../../context/SurveyPricesContext';
import { SurveyTableFooter } from './SurveyTableFooter';
import { SurveyDataTable } from './SurveyDataTable';
import { When } from '../common';

type SurveyItemsTableProps = {
    survey: PriceSurvey;
    authUser: User | null;
    isLoading: boolean;
    onRefresh: () => void | Promise<void>;
    onAddCompetitor: () => void;
    onCompetitorClick?: (data: CompetitorDrawerData) => void;
    onRemoveCompetitor?: (priceSurveyCompetitorId: string) => void;
    onDivisionalManagerActionsReady?: (
        actions: {
            submitSurvey: () => void;
            canSubmit: boolean;
            isSubmitting: boolean;
            hasChanges: boolean;
        } | null,
    ) => void;
    pricesMap: Map<string, SurveyPriceCell>;
    activeCompetitors: PriceSurveyCompetitor[];
    updateCell: (
        priceId: string,
        update: { price?: number | null; isNA?: boolean },
    ) => void;
    dmProgressResetKey?: number;
};

export function SurveyItemsTable({
    survey,
    authUser,
    isLoading,
    onRefresh,
    onAddCompetitor,
    onCompetitorClick,
    onRemoveCompetitor,
    onDivisionalManagerActionsReady,
    pricesMap,
    activeCompetitors,
    updateCell,
    dmProgressResetKey = 0,
}: SurveyItemsTableProps) {
    const { isSaving } = useSurveyPricesContext();
    const { open: openRequestDrawer } = useRequestDrawer();
    const isStoreManagerUser = checkUserRole(authUser, [
        UserRole.STORE_MANAGER,
    ]);
    const isDivisionalManagerUser = checkUserRole(authUser, [
        UserRole.DIVISIONAL_MANAGER,
    ]);

    const showDMView = !isStoreManagerUser;

    const canAddCompetitor = isStoreManagerUser;
    const inDivisionalManagerReview = isSurveyInDivisionalManagerReview(survey);

    const isReviewer = isDivisionalManagerUser || isUserAdmin(authUser);
    const showDivisionalManagerProposedSelling =
        isReviewer && inDivisionalManagerReview;

    const competitorCellsStatic =
        isStoreManagerUser && isSurveyStoreManagerEditingLocked(survey);

    const priceHandlers = useMemo(
        () => ({
            updateCell,
            isSaving,
        }),
        [updateCell, isSaving],
    );

    const competitorIdMap = useMemo(() => {
        const map = new Map<string, string>();
        activeCompetitors?.forEach(sc => {
            map.set(sc.id, sc.competitorId);
        });
        return map;
    }, [activeCompetitors]);

    const productIds = useMemo(
        () =>
            Array.from(
                new Set(
                    (survey?.surveyItems ?? [])
                        .map(item => item.product?.id ?? item.productId ?? '')
                        .filter((id): id is string => !!id),
                ),
            ),
        [survey?.surveyItems],
    );
    const surveyCompleted = survey?.status === 'Completed';
    const canCollapseCompetitors =
        surveyCompleted && activeCompetitors.length > 0;
    const [competitorsCollapsed, setCompetitorsCollapsed] = useState(true);
    const [categoryCollapsed, setCategoryCollapsed] = useState(false);
    const handleToggleCategoryCollapsed = useCallback(() => {
        setCategoryCollapsed(c => !c);
    }, []);

    const handleToggleCompetitorsCollapsed = useCallback(() => {
        setCompetitorsCollapsed(c => !c);
    }, []);

    const anyCollapsibleColumnCollapsed = useMemo(
        () =>
            categoryCollapsed ||
            (canCollapseCompetitors && competitorsCollapsed),
        [categoryCollapsed, canCollapseCompetitors, competitorsCollapsed],
    );

    const handleToggleAllCollapsibleColumns = useCallback(() => {
        if (anyCollapsibleColumnCollapsed) {
            setCategoryCollapsed(false);
            if (canCollapseCompetitors) {
                setCompetitorsCollapsed(false);
            }
            return;
        }

        setCategoryCollapsed(true);
        if (canCollapseCompetitors) {
            setCompetitorsCollapsed(true);
        }
    }, [anyCollapsibleColumnCollapsed, canCollapseCompetitors]);
    const showDMExtraColumns =
        showDMView && (surveyCompleted || inDivisionalManagerReview);
    const salesMaintenance = useSalesDataMaintenance();
    const shouldLoadProductSales = showDMExtraColumns;
    const productSalesReferenceDate = useMemo(
        () => survey?.dueBy ?? dayjs().toISOString(),
        [survey?.dueBy],
    );
    const productIdsForSales = shouldLoadProductSales ? productIds : [];
    const productSalesScopeInput = useMemo<
        ProductSalesScopeInput | undefined
    >(() => {
        const advertisingZoneId = getSurveyAdvertisingZoneId(survey);
        return advertisingZoneId ? { advertisingZoneId } : undefined;
    }, [survey]);
    const productSalesData = useProductSalesForSurveyItems(
        productIdsForSales,
        productSalesReferenceDate,
        productSalesScopeInput,
    );

    const isCompetitorEntryComplete = (
        priceId: string | undefined,
    ): boolean => {
        if (!priceId) return false;
        const cell = pricesMap.get(priceId);
        if (!cell) return false;
        if (cell.isNA) return true;
        return (
            cell.currentPrice !== null &&
            cell.currentPrice !== undefined &&
            cell.currentPrice > 0
        );
    };

    const pricesByItemAndCompetitor = useMemo(() => {
        const map = new Map<
            string,
            { priceId: string; cell: SurveyPriceCell }
        >();
        pricesMap.forEach((cell, priceId) => {
            const key = `${cell.surveyItemId}:${cell.competitorId}`;
            map.set(key, { priceId, cell });
        });
        return map;
    }, [pricesMap]);

    const tableData: SurveyItemRow[] = useMemo(() => {
        if (!survey?.surveyItems) return [];

        return survey.surveyItems.map(item => {
            const competitorPricesMap: Record<
                string,
                {
                    price: number | null;
                    priceId?: string;
                    priceSurveyCompetitorId?: string;
                    isNA?: boolean;
                }
            > = {};

            item.surveyItemPrices?.forEach(sip => {
                const competitorId = competitorIdMap.get(
                    sip.priceSurveyCompetitorId || '',
                );
                if (competitorId && sip.id) {
                    const cell = pricesMap.get(sip.id);
                    if (cell) {
                        competitorPricesMap[competitorId] = {
                            price: cell.currentPrice,
                            priceId: sip.id,
                            priceSurveyCompetitorId:
                                sip.priceSurveyCompetitorId || undefined,
                            isNA: cell.isNA,
                        };
                    } else {
                        competitorPricesMap[competitorId] = {
                            price: sip.price ?? null,
                            priceId: sip.id,
                            priceSurveyCompetitorId:
                                sip.priceSurveyCompetitorId || undefined,
                            isNA: sip.isNA ?? false,
                        };
                    }
                }
            });

            activeCompetitors.forEach(sc => {
                if (!competitorPricesMap[sc.competitorId]) {
                    const key = `${item.id}:${sc.competitorId}`;
                    const found = pricesByItemAndCompetitor.get(key);

                    if (found) {
                        const { cell } = found;
                        competitorPricesMap[sc.competitorId] = {
                            price: cell.currentPrice,
                            priceId: found.priceId,
                            priceSurveyCompetitorId:
                                cell.priceSurveyCompetitorId || undefined,
                            isNA: cell.isNA,
                        };
                    } else {
                        competitorPricesMap[sc.competitorId] = {
                            price: null,
                            priceId: undefined,
                            priceSurveyCompetitorId: undefined,
                            isNA: false,
                        };
                    }
                }
            });

            const competitors = activeCompetitors || [];
            const isComplete =
                competitors.length > 0 &&
                competitors.every(sc => {
                    const priceData = competitorPricesMap[sc.competitorId];
                    return isCompetitorEntryComplete(priceData?.priceId);
                });

            const currency =
                item.product?.sellingPriceCurrency?.trim() || 'ZAR';
            const livePricing = readSurveyItemLivePricing(item);
            const { currentCashbuildPrice, currentPnlPrice } = livePricing;

            return {
                id: item.id,
                productId: item.product?.id ?? item.productId ?? '',
                rank: item.rank,
                category: item.category?.name || '',
                sku: item.product?.sku || '',
                description: item.product?.description || '',
                currentCostPrice:
                    livePricing.zoneHighestCostPrice ??
                    item.product?.currentCostPrice ??
                    null,
                cashbuildCostPrice: livePricing.cashbuildCostPrice,
                pnlCostPrice: livePricing.pnlCostPrice,
                benchmarkMargin: item.category?.benchmarkMargin ?? null,
                isComplete,
                currency,
                currentPrice:
                    currentCashbuildPrice ??
                    currentPnlPrice ??
                    item.product?.currentSellingPrice ??
                    null,
                currentCashbuildPrice,
                currentPnlPrice,
                competitorPrices: competitorPricesMap,
                suggestedPrice: item.suggestedPrice ?? null,
                resolvedVatPercentage: item.resolvedVatPercentage ?? null,
                proposedSellingPrice: item.proposedSellingPrice ?? null,
                isDeclinedForPriceChange:
                    item.isDeclinedForPriceChange ?? false,
                declinedReason: item.declinedReason ?? null,
                approvedForPriceChange: item.approvedForPriceChange ?? false,
                priceChangeRequestId: item.priceChangeRequestId ?? null,
                priceChangeRequest: item.priceChangeRequest ?? null,
            };
        });
    }, [
        survey?.surveyItems,
        activeCompetitors,
        competitorIdMap,
        pricesMap,
        pricesByItemAndCompetitor,
    ]);

    const surveyItemsColumns = useMemo(
        () =>
            getSurveyItemsColumns({
                showDMColumns: showDMView,
                showProposedSelling: showDivisionalManagerProposedSelling,
                showDMReviewColumns: showDMExtraColumns,
                surveyCompetitors: activeCompetitors,
                isStoreManager: isStoreManagerUser,
                advertisingZoneStores: survey.advertisingZone?.stores ?? [],
                priceHandlers: isStoreManagerUser ? priceHandlers : undefined,
                onCompetitorClick,
                onRemoveCompetitor: isStoreManagerUser
                    ? onRemoveCompetitor
                    : undefined,
                competitorCellsStatic,
                surveyId: survey.id,
                surveyCompleted,
                competitorsCollapsed:
                    canCollapseCompetitors && competitorsCollapsed,
                onToggleCompetitorsCollapsed: canCollapseCompetitors
                    ? handleToggleCompetitorsCollapsed
                    : undefined,
                categoryCollapsed,
                onToggleCategoryCollapsed: handleToggleCategoryCollapsed,
                productSalesData: shouldLoadProductSales
                    ? {
                          thisYear: productSalesData.thisYear,
                          lastYear: productSalesData.lastYear,
                      }
                    : undefined,
                productSalesLoading: shouldLoadProductSales
                    ? productSalesData.loading
                    : false,
                productSalesWarmingIds: shouldLoadProductSales
                    ? productSalesData.warmingProductIds
                    : undefined,
                productSalesWarmTimedOut: shouldLoadProductSales
                    ? productSalesData.warmTimedOut
                    : false,
                salesMaintenanceProductIds: shouldLoadProductSales
                    ? productSalesData.maintenanceProductIds
                    : undefined,
                salesMaintenanceCopy: salesMaintenance.isActive
                    ? salesMaintenance.message
                    : '',
                salesMaintenanceEndsAtLabel: salesMaintenance.endsAtLabel,
            }),
        [
            showDMView,
            shouldLoadProductSales,
            showDivisionalManagerProposedSelling,
            activeCompetitors,
            survey.id,
            survey?.status,
            surveyCompleted,
            canCollapseCompetitors,
            competitorsCollapsed,
            categoryCollapsed,
            handleToggleCategoryCollapsed,
            handleToggleCompetitorsCollapsed,
            isStoreManagerUser,
            priceHandlers,
            onCompetitorClick,
            onRemoveCompetitor,
            competitorCellsStatic,
            productSalesData.thisYear,
            productSalesData.lastYear,
            productSalesData.loading,
            productSalesData.warmingProductIds,
            productSalesData.warmTimedOut,
            salesMaintenance.isActive,
            salesMaintenance.message,
            salesMaintenance.endsAtLabel,
            productSalesData.maintenanceProductIds,
            survey.advertisingZone?.stores,
        ],
    );

    const { refetch: refetchProductSalesForSurvey } = productSalesData;

    const performSurveyTableRefresh = useCallback(async () => {
        if (shouldLoadProductSales) {
            await refetchProductSalesForSurvey();
        }
        await onRefresh();
    }, [shouldLoadProductSales, refetchProductSalesForSurvey, onRefresh]);

    const runSurveyTableRefreshSafe = useCallback(
        (): Promise<void> =>
            performSurveyTableRefresh().catch(error => {
                console.error(
                    '[SurveyItemsTable] survey table refresh failed:',
                    getErrorMessage(error),
                );
            }),
        [performSurveyTableRefresh],
    );

    const handleSurveyToolbarRefreshClick = useCallback(() => {
        runSurveyTableRefreshSafe();
    }, [runSurveyTableRefreshSafe]);

    const tableContent = (
        <div className="w-full bg-white shadow-md rounded-xl flex flex-col flex-1 min-h-0">
            <Stack p="md" pb="sm" gap={0} flex={0}>
                <Flex w="100%" align="center" justify="space-between">
                    <Text size="xl" fw={600}>
                        Survey
                    </Text>
                    <Group gap="xs">
                        <Tooltip
                            label={
                                anyCollapsibleColumnCollapsed
                                    ? 'Show collapsible columns'
                                    : 'Hide collapsible columns'
                            }
                            withArrow
                            position="bottom"
                            openDelay={400}
                        >
                            <Button
                                variant="outline"
                                radius="xl"
                                onClick={handleToggleAllCollapsibleColumns}
                                className="border-primary text-primary hover:bg-primary/10 text-sm font-medium h-auto py-2.5 px-4 w-fit"
                                aria-expanded={!anyCollapsibleColumnCollapsed}
                                aria-label={
                                    anyCollapsibleColumnCollapsed
                                        ? 'Show collapsible columns'
                                        : 'Hide collapsible columns'
                                }
                            >
                                <Group gap={8} align="center" wrap="nowrap">
                                    {anyCollapsibleColumnCollapsed ? (
                                        <IconEye size={18} stroke={2} />
                                    ) : (
                                        <IconEyeOff size={18} stroke={2} />
                                    )}
                                    <span>
                                        {anyCollapsibleColumnCollapsed
                                            ? 'Show collapsible columns'
                                            : 'Hide collapsible columns'}
                                    </span>
                                </Group>
                            </Button>
                        </Tooltip>
                        <When
                            condition={
                                canAddCompetitor && !competitorCellsStatic
                            }
                        >
                            <Button
                                onClick={onAddCompetitor}
                                radius="xl"
                                className="bg-primary text-white hover:bg-primary/90 border-0 text-sm font-medium h-auto py-2.5 px-4 w-fit"
                            >
                                <Group gap={10} align="center" wrap="nowrap">
                                    <span className="leading-none mt-[-1px]">
                                        Add competitor
                                    </span>
                                    <IconCirclePlus size={16} />
                                </Group>
                            </Button>
                        </When>
                        <Button
                            variant="subtle"
                            className="text-primary rounded-md p-1 w-min h-min"
                            onClick={handleSurveyToolbarRefreshClick}
                        >
                            <IconRefresh size={24} />
                        </Button>
                    </Group>
                </Flex>
            </Stack>
            <Box className="flex-1 px-4 pb-4 min-h-0 flex flex-col gap-2">
                <PageZoomHost>
                    <SurveyDataTable
                        tableData={tableData}
                        surveyItemsColumns={surveyItemsColumns}
                        isLoading={isLoading}
                        showDivisionalManagerProposedSelling={
                            showDivisionalManagerProposedSelling
                        }
                        onOpenPriceChangeRequest={
                            surveyCompleted
                                ? requestId =>
                                      openRequestDrawer(null, requestId)
                                : undefined
                        }
                    />
                </PageZoomHost>
                <SurveyTableFooter
                    onDivisionalManagerActionsReady={
                        onDivisionalManagerActionsReady
                    }
                    showSaveProgress={
                        (isStoreManagerUser && !competitorCellsStatic) ||
                        showDivisionalManagerProposedSelling
                    }
                />
            </Box>
        </div>
    );

    if (showDivisionalManagerProposedSelling) {
        return (
            <DivisionalManagerSurveyProgressProvider
                key={dmProgressResetKey}
                surveyId={survey.id}
                surveyItems={tableData}
                onSuccess={runSurveyTableRefreshSafe}
            >
                {tableContent}
            </DivisionalManagerSurveyProgressProvider>
        );
    }

    return tableContent;
}
